import { useCallback, useEffect, useMemo, useState } from 'react';

import { AvailabilitySlot } from '@/types/appointment';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { CreateRecurrenceDto, OccurrenceIssue, RecurrencePlan } from '@/types/recurrence';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatSlotTime, zonedDateKey, zonedWeekday } from '@/utils/format-appointment';

/**
 * A agenda que se monta tocando nos horários livres — uma lógica só, usada
 * pelo prestador (contrato) e pelo cliente (página do serviço, logado ou
 * convidado). Quem manda é o fluxo do contrato: repetição por dia+hora, datas
 * liberadas à mão, trocas no lugar delas e teto de cota.
 */

export const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
/** Sem "-feira": o rótulo divide a largura com a outra pill. */
export const WEEKDAYS_LONG = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
];

const WEEKS_PER_MONTH = 4;

/** Por que uma data da grade não vira sessão — texto curto pro preview. */
export const OCCURRENCE_ISSUE_LABEL: Record<OccurrenceIssue, string> = {
  PAST: 'já passou',
  OUTSIDE_GRID: 'fora dos horários de atendimento',
  BLOCKED: 'prestador bloqueou o período',
  FULL: 'horário já ocupado',
  QUOTA: 'fora da cota deste ciclo',
};

/** yyyy-MM-dd → dd/MM, sem criar Date em UTC. */
export function formatShortDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

/** Chave de dedupe de um slot: dia da semana + horário. */
export const slotKey = (s: { startAt: string }) =>
  `${zonedWeekday(s.startAt)}-${formatSlotTime(s.startAt)}`;

/** Compara por instante: o ISO do preview e o da agenda vêm formatados diferente. */
export const sameInstant = (a: string, b: string) =>
  new Date(a).getTime() === new Date(b).getTime();

/** Quanto da cota N horários consomem — repetindo toda semana pesa ~4x/mês cada. */
export function gradeSessionsPerPeriod(
  slotCount: number,
  period: QUOTA_PERIOD,
  repeat: boolean,
): number {
  if (slotCount === 0) return 0;
  if (!repeat) return slotCount; // datas soltas: uma sessão cada.
  return period === QUOTA_PERIOD.WEEK ? slotCount : slotCount * WEEKS_PER_MONTH;
}

/** Quantos horários "toda semana" cabem na cota (cada um pesa ~4x/mês). */
export function maxRepeatSlotsForQuota(
  quota: number | undefined | null,
  period: QUOTA_PERIOD,
): number | undefined {
  if (!quota) return undefined;
  return period === QUOTA_PERIOD.WEEK ? quota : Math.floor(quota / WEEKS_PER_MONTH);
}

/**
 * Agenda montada a partir dos horários que a pessoa tocou na própria grade:
 * uma linha por (dia da semana, hora). O período fica com o backend, que corta
 * no horizonte do serviço.
 */
export function buildRecurrenceDto(
  serviceId: string,
  slots: AvailabilitySlot[],
  intervalWeeks = 1,
  /** "yyyy-MM-dd": início do contrato. Ancora o horizonte e o ciclo de cota. */
  startDate?: string,
  /** Ocorrências (ISO) liberadas à mão: a linha do mesmo dia+hora pula essas datas. */
  skips: string[] = [],
): CreateRecurrenceDto | null {
  if (slots.length === 0) return null;
  const byKey = new Map<
    string,
    { weekday: number; startTime: string; fromDate: string; skipDates?: string[] }
  >();
  for (const slot of slots) {
    const weekday = zonedWeekday(slot.startAt);
    const startTime = formatSlotTime(slot.startAt);
    // A data tocada é onde a repetição começa — nunca antes dela.
    const fromDate = zonedDateKey(slot.startAt);
    const key = `${weekday}-${startTime}`;
    const existing = byKey.get(key);
    if (existing) {
      if (fromDate < existing.fromDate) existing.fromDate = fromDate;
      continue;
    }
    byKey.set(key, { weekday, startTime, fromDate });
  }
  for (const skip of skips) {
    const row = byKey.get(`${zonedWeekday(skip)}-${formatSlotTime(skip)}`);
    if (row) row.skipDates = [...(row.skipDates ?? []), zonedDateKey(skip)];
  }
  return {
    serviceId,
    frequency: 'WEEKLY',
    intervalWeeks,
    slots: [...byKey.values()],
    ...(startDate ? { startDate } : {}),
  };
}

/** yyyy-MM-dd + n dias, sem passar por UTC. */
function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Datas soltas pela mesma porta da recorrência: uma linha por (dia, hora) que
 * começa na primeira data escolhida, vai até a última de todas e pula toda
 * semana que a pessoa não escolheu. O backend gera exatamente as datas
 * tocadas — é como "só esse dia" vira assinatura ou recorrência sem um
 * endpoint novo.
 */
export function buildOneOffDto(
  serviceId: string,
  slots: AvailabilitySlot[],
  startDate?: string,
): CreateRecurrenceDto | null {
  if (slots.length === 0) return null;
  const byKey = new Map<string, { weekday: number; startTime: string; dates: string[] }>();
  for (const slot of slots) {
    const key = slotKey(slot);
    const row = byKey.get(key) ?? {
      weekday: zonedWeekday(slot.startAt),
      startTime: formatSlotTime(slot.startAt),
      dates: [],
    };
    row.dates.push(zonedDateKey(slot.startAt));
    byKey.set(key, row);
  }
  const endDate = [...byKey.values()]
    .flatMap((r) => r.dates)
    .reduce((a, b) => (a > b ? a : b));
  return {
    serviceId,
    frequency: 'WEEKLY',
    intervalWeeks: 1,
    slots: [...byKey.values()].map((row) => {
      const chosen = new Set(row.dates);
      const fromDate = row.dates.reduce((a, b) => (a < b ? a : b));
      const skipDates: string[] = [];
      for (let date = fromDate; date <= endDate; date = addDays(date, 7)) {
        if (!chosen.has(date)) skipDates.push(date);
      }
      return {
        weekday: row.weekday,
        startTime: row.startTime,
        fromDate,
        ...(skipDates.length ? { skipDates } : {}),
      };
    }),
    endDate,
    ...(startDate ? { startDate } : {}),
  };
}

interface PlanState {
  plan: RecurrencePlan | null;
  previewing: boolean;
  error: string | null;
}

/**
 * Prévia da agenda enquanto a pessoa escolhe — sem botão "ver agenda", que era
 * um passo a mais para saber o óbvio (quantas sessões e até quando). Debounce
 * para não disparar a cada toque na grade.
 */
export function useRepeatPlan({
  dto,
  enabled,
  preview,
}: {
  /** Agenda pronta: repetição ou datas soltas — a prévia é a mesma. */
  dto: CreateRecurrenceDto | null;
  enabled: boolean;
  preview: (dto: CreateRecurrenceDto) => Promise<RecurrencePlan>;
}): PlanState {
  const [state, setState] = useState<PlanState>({
    plan: null,
    previewing: false,
    error: null,
  });

  // Assinatura estável da seleção: só refaz a prévia quando ela muda de verdade.
  const signature = useMemo(() => (dto ? JSON.stringify(dto) : ''), [dto]);

  useEffect(() => {
    if (!enabled || !dto) {
      setState({ plan: null, previewing: false, error: null });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, previewing: true, error: null }));
    const timer = setTimeout(() => {
      preview(dto)
        .then((plan) => {
          if (!cancelled) setState({ plan, previewing: false, error: null });
        })
        .catch((err: any) => {
          if (cancelled) return;
          setState({
            plan: null,
            previewing: false,
            error: getApiErrorMessage(err, 'Não foi possível montar a agenda.'),
          });
        });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `signature` já resume o dto inteiro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled]);

  return state;
}

export interface UseScheduleGradeOptions {
  serviceId: string;
  /** Prévia da agenda: prestador, cliente logado ou convidado — a mesma forma. */
  preview: (dto: CreateRecurrenceDto) => Promise<RecurrencePlan>;
  /** Serviço aceita repetir? Falso deixa só datas soltas. */
  canRepeat: boolean;
  /** Assinatura: a cota limita quantas sessões cabem no ciclo. */
  isSubscription?: boolean;
  /**
   * Já é assinante ativo: marcar é gastar cota, não entrar no plano. Sem isso
   * a escolha viraria um pedido de contrato novo e o backend recusaria com
   * "Você já assina este serviço.".
   */
  alreadySubscribed?: boolean;
  /** Cota do ciclo; ausente = sem teto. */
  quota?: number | null;
  quotaPeriod?: QUOTA_PERIOD;
  /** "yyyy-MM-dd": início do contrato. Sem isso o backend ancora em "hoje". */
  startDate?: string;
  intervalWeeks?: number;
  /** Começa repetindo (assinatura ainda não assinada). */
  initialRepeat?: boolean;
  /**
   * Sem repetir, a seleção é de um horário só — é o caso do cliente marcando
   * um atendimento avulso. O prestador pré-marca várias datas de uma vez.
   */
  singleWhenOnce?: boolean;
  /** Aviso de teto batido (toast na tela). */
  onWarn?: (message: string) => void;
}

export interface ScheduleGradeState {
  /** Linhas da grade: com repetir, a data em que cada dia+hora começa. */
  slots: AvailabilitySlot[];
  /** Datas avulsas que entram no lugar de uma liberada. */
  extras: AvailabilitySlot[];
  /** Ocorrências (ISO) liberadas à mão. */
  skips: string[];
  repeat: boolean;
  setRepeat: (next: boolean) => void;
  /** Substitui a seleção inteira (edição, reset de contexto). */
  setSlots: (slots: AvailabilitySlot[]) => void;
  toggleSlot: (slot: AvailabilitySlot) => void;
  /** Tira a repetição inteira de um dia+hora. */
  removeRow: (key: string) => void;
  /** Devolve uma data liberada à repetição. */
  restoreSkip: (startAt: string) => void;
  clear: () => void;
  /** Último horário tocado — o "selecionado" da timeline. */
  selected: AvailabilitySlot | null;
  /** Dias que o calendário pinta como marcados. */
  markedDates: Set<string>;
  /** Horários pintados como escolhidos em qualquer dia que abrir. */
  highlightedStarts: Set<string>;
  plan: RecurrencePlan | null;
  previewing: boolean;
  error: string | null;
  /** Ocorrências que a grade marca de fato (preview menos as liberadas). */
  occurrences: RecurrencePlan['occurrences'];
  /** Datas que não viram sessão por conflito real, já com o motivo legível. */
  skipped: { key: string; when: string; label: string }[];
  overQuota: boolean;
  count: number;
  /** Sessões por período em regime, limitadas pela cota. */
  sessionsPerRegime: number;
  /** Quantos horários "toda semana" cabem na cota. */
  maxRepeatSlots: number | undefined;
  quota: number | undefined;
  quotaPeriod: QUOTA_PERIOD;
  periodWord: string;
  isSubscription: boolean;
  canRepeat: boolean;
  /** Agenda pronta para o backend; nula sem horários ou sem repetir. */
  dto: CreateRecurrenceDto | null;
}

/**
 * Estado da grade. A tela só fornece a disponibilidade e os termos (cota,
 * assinatura); toda a mecânica de tocar, liberar e trocar mora aqui.
 */
export function useScheduleGrade({
  serviceId,
  preview,
  canRepeat,
  isSubscription = false,
  alreadySubscribed = false,
  quota,
  quotaPeriod = QUOTA_PERIOD.MONTH,
  startDate,
  intervalWeeks = 1,
  initialRepeat = false,
  singleWhenOnce = false,
  onWarn,
}: UseScheduleGradeOptions): ScheduleGradeState {
  const [slots, setSlotsState] = useState<AvailabilitySlot[]>([]);
  const [repeat, setRepeatState] = useState(initialRepeat && canRepeat);
  const [skips, setSkips] = useState<string[]>([]);
  const [extras, setExtras] = useState<AvailabilitySlot[]>([]);

  const effectiveQuota = quota ?? undefined;
  const periodWord = quotaPeriod === QUOTA_PERIOD.WEEK ? 'semana' : 'mês';
  const repeating = repeat && canRepeat;

  // O serviço manda: assinatura nova nasce repetindo, serviço que não aceita
  // recorrência nunca repete.
  useEffect(() => {
    setRepeatState(canRepeat ? initialRepeat : false);
    setSkips([]);
    setExtras([]);
  }, [canRepeat, initialRepeat]);

  const maxRepeatSlots =
    isSubscription && repeating ? maxRepeatSlotsForQuota(effectiveQuota, quotaPeriod) : undefined;

  /**
   * A agenda pronta para o backend. Repetindo é a grade semanal; sem repetir,
   * as datas escolhidas viram a mesma coisa limitada àquelas datas — quem
   * marca uma sessão só não precisa de outro caminho.
   */
  // Quem já assina não monta plano nenhum: cada sessão é um agendamento que
  // sai da cota do ciclo.
  const buildsPlan = isSubscription && !alreadySubscribed;

  const dto = useMemo(
    () =>
      repeating
        ? buildRecurrenceDto(serviceId, slots, intervalWeeks, startDate, skips)
        : // Sem repetir, só a assinatura nova passa por aqui: as datas
          // escolhidas entram no plano. Sessão avulsa continua sendo um
          // agendamento.
          buildsPlan
          ? buildOneOffDto(serviceId, slots, startDate)
          : null,
    [repeating, buildsPlan, serviceId, slots, intervalWeeks, startDate, skips],
  );

  const planState = useRepeatPlan({
    dto,
    enabled: slots.length > 0 && (repeating || buildsPlan),
    preview,
  });

  const occurrences = useMemo(() => {
    if (!repeating || !planState.plan) return [];
    return planState.plan.occurrences.filter(
      (o) => o.available && !skips.some((s) => sameInstant(s, o.startAt)),
    );
  }, [repeating, planState.plan, skips]);

  const markedDates = useMemo(() => {
    if (repeating && planState.plan) {
      return new Set([...occurrences, ...extras].map((o) => zonedDateKey(o.startAt)));
    }
    return new Set(slots.map((s) => zonedDateKey(s.startAt)));
  }, [repeating, planState.plan, occurrences, extras, slots]);

  const highlightedStarts = useMemo(() => {
    const starts = slots.map((s) => s.startAt);
    if (repeating) {
      starts.push(...occurrences.map((o) => o.startAt), ...extras.map((s) => s.startAt));
    }
    return new Set(starts);
  }, [repeating, occurrences, extras, slots]);

  const count = slots.length;
  const consumes = gradeSessionsPerPeriod(count, quotaPeriod, repeating);

  /**
   * Sessões por período em regime — não o total bruto da janela de preview,
   * que quase nunca cobre um período cheio (começa no meio) e por isso
   * subconta a primeira janela.
   */
  const sessionsPerRegime =
    consumes === 0 ? 0 : Math.min(consumes, effectiveQuota ?? consumes);

  /**
   * Datas que NÃO viram sessão por um conflito real. `QUOTA` fica de fora: as
   * que sobram do teto não são um problema, são o teto funcionando.
   */
  const skipped = useMemo(() => {
    const list = planState.plan?.occurrences ?? [];
    return list
      .filter((o) => !o.available && o.reason && o.reason !== 'QUOTA')
      .map((o) => ({
        key: o.startAt,
        when: `${formatShortDate(zonedDateKey(o.startAt))} ${formatSlotTime(o.startAt)}`,
        label: OCCURRENCE_ISSUE_LABEL[o.reason as OccurrenceIssue],
      }));
  }, [planState.plan]);

  const overQuota =
    isSubscription &&
    !!effectiveQuota &&
    (repeating
      ? consumes > effectiveQuota || occurrences.length + extras.length > effectiveQuota
      : slots.length > effectiveQuota);

  const warnQuotaFull = useCallback(() => {
    onWarn?.(
      `A cota cobre ${effectiveQuota} ${
        effectiveQuota === 1 ? 'sessão' : 'sessões'
      } por ${periodWord}.`,
    );
  }, [onWarn, effectiveQuota, periodWord]);

  /** Tira a repetição inteira de um dia+hora, com as datas liberadas dela. */
  const removeRow = useCallback(
    (key: string) => {
      setSlotsState((prev) => prev.filter((s) => slotKey(s) !== key));
      setSkips((prevSkips) => {
        const next = prevSkips.filter((s) => slotKey({ startAt: s }) !== key);
        // Troca só existe no lugar de uma data liberada.
        setExtras((prevExtras) => prevExtras.slice(0, next.length));
        return next;
      });
    },
    [],
  );

  /** Devolve uma data liberada à repetição; a troca que sobrar sai junto. */
  const restoreSkip = useCallback((startAt: string) => {
    setSkips((prev) => {
      const next = prev.filter((s) => !sameInstant(s, startAt));
      setExtras((prevExtras) => prevExtras.slice(0, next.length));
      return next;
    });
  }, []);

  /**
   * Toque na agenda. Sem "repetir": liga/desliga aquela data (ou troca a
   * escolha, quando a seleção é de um horário só). Com "repetir", nesta ordem:
   * desfaz troca; devolve data liberada; na data em que a linha começa, tira a
   * repetição; num dia que repete, libera só ele; com data liberada pendente,
   * o horário livre vira a troca; senão, nova linha.
   */
  const toggleSlot = useCallback(
    (slot: AvailabilitySlot) => {
      const at = slot.startAt;

      if (!repeating) {
        if (singleWhenOnce && !canRepeat) {
          setSlotsState((prev) =>
            prev.length === 1 && sameInstant(prev[0].startAt, at) ? prev : [slot],
          );
          return;
        }
        const picked = slots.some((s) => sameInstant(s.startAt, at));
        if (!picked && isSubscription && effectiveQuota && slots.length >= effectiveQuota) {
          warnQuotaFull();
          return;
        }
        setSlotsState((prev) =>
          picked ? prev.filter((s) => !sameInstant(s.startAt, at)) : [...prev, slot],
        );
        return;
      }

      if (extras.some((s) => sameInstant(s.startAt, at))) {
        setExtras((prev) => prev.filter((s) => !sameInstant(s.startAt, at)));
        return;
      }
      if (skips.some((s) => sameInstant(s, at))) {
        restoreSkip(at);
        return;
      }
      const key = slotKey(slot);
      const row = slots.find((s) => slotKey(s) === key);
      if (row && sameInstant(row.startAt, at)) {
        removeRow(key);
        return;
      }
      if (occurrences.some((o) => sameInstant(o.startAt, at))) {
        setSkips((prev) => [...prev, at]);
        return;
      }
      if (extras.length < skips.length) {
        // Liberar uma data pode trazer de volta a que a cota cortava: a troca
        // não pode passar do teto.
        if (
          isSubscription &&
          effectiveQuota &&
          occurrences.length + extras.length >= effectiveQuota
        ) {
          warnQuotaFull();
          return;
        }
        setExtras((prev) => [...prev, slot]);
        return;
      }
      if (row) {
        // Mesmo dia+hora antes do início: a repetição passa a começar ali.
        if (at < row.startAt) {
          setSlotsState((prev) => prev.map((s) => (s === row ? slot : s)));
        } else {
          onWarn?.('Esse horário já se repete toda semana.');
        }
        return;
      }
      if (maxRepeatSlots != null && slots.length >= maxRepeatSlots) {
        onWarn?.(
          `Repetindo toda semana, a cota cobre ${maxRepeatSlots} ${
            maxRepeatSlots > 1 ? 'horários' : 'horário'
          }.`,
        );
        return;
      }
      setSlotsState((prev) => [...prev, slot]);
    },
    [
      repeating,
      canRepeat,
      singleWhenOnce,
      slots,
      extras,
      skips,
      occurrences,
      isSubscription,
      effectiveQuota,
      maxRepeatSlots,
      warnQuotaFull,
      onWarn,
      removeRow,
      restoreSkip,
    ],
  );

  /**
   * Ligar "repetir": um horário por dia+hora (a data mais cedo) e derruba o
   * que estoura a cota. Desligar: as trocas viram datas soltas como as outras.
   */
  const setRepeat = useCallback(
    (next: boolean) => {
      setRepeatState(next);
      setSkips([]);
      setExtras([]);
      if (!next) {
        setSlotsState((prev) =>
          singleWhenOnce && !canRepeat ? prev.slice(0, 1) : [...prev, ...extras],
        );
        return;
      }
      const cap = maxRepeatSlotsForQuota(
        isSubscription ? effectiveQuota : undefined,
        quotaPeriod,
      );
      setSlotsState((prev) => {
        const kept = new Map<string, AvailabilitySlot>();
        for (const s of [...prev].sort((a, b) => a.startAt.localeCompare(b.startAt))) {
          const k = slotKey(s);
          if (kept.has(k) || (cap != null && kept.size >= cap)) continue;
          kept.set(k, s);
        }
        return [...kept.values()];
      });
    },
    [extras, isSubscription, effectiveQuota, quotaPeriod, singleWhenOnce, canRepeat],
  );

  const setSlots = useCallback((next: AvailabilitySlot[]) => {
    setSlotsState(next);
    setSkips([]);
    setExtras([]);
  }, []);

  const clear = useCallback(() => setSlots([]), [setSlots]);

  return {
    slots,
    extras,
    skips,
    repeat: repeating,
    setRepeat,
    setSlots,
    toggleSlot,
    removeRow,
    restoreSkip,
    clear,
    selected: slots.length > 0 ? slots[slots.length - 1] : null,
    markedDates,
    highlightedStarts,
    plan: planState.plan,
    previewing: planState.previewing,
    error: planState.error,
    occurrences,
    skipped,
    overQuota,
    count,
    sessionsPerRegime,
    maxRepeatSlots,
    quota: effectiveQuota,
    quotaPeriod,
    periodWord,
    isSubscription,
    canRepeat,
    dto,
  };
}
